/**
 * WaterBlockEntity - Pushable block that fills water
 *
 * Water blocks don't move autonomously - they only move when pushed
 * by the player or other entities. When pushed into water, they fill
 * the water tile and disappear.
 */

import { BaseTile, MoveContext, MoveResult, TextureDescriptor } from "./BaseTile";
import { TILE, isEmpty, isArrowTile, getArrowDirection } from "../constants/gameConstants";
import { TileIndex } from "../types";
import { Direction } from "../utils/Direction";
import { GAME_CONFIG } from "../config";


export class WaterBlockEntity extends BaseTile {
  // Forced arrow movement state
  private timeSinceLastMove: number = 0;
  private forcedDirection?: Direction;
  private pendingMove?: Direction;

  constructor({
    row,
    col,
    id,
  }: {
    row: number;
    col: number;
    id?: string;
  }) {
    super(id ?? `waterblock-${col}-${row}-${Date.now()}`, col, row);
  }

  tileType(): TileIndex {
    return TILE.WATER_BLOCK;
  }

  textureDescriptor(): TextureDescriptor {
    return { type: "png", path: "tiles/water-block.png" };
  }

  isEntity(): boolean {
    return true; // Water block is a movable entity (can be pushed)
  }

  /**
   * Queue a move direction
   */
  queueMove(direction: Direction): void {
    this.pendingMove = direction;
  }

  /**
   * Clear any pending move
   */
  clearPendingMove(): void {
    this.pendingMove = undefined;
  }

  /**
   * Set forced movement direction (when entering arrow tile)
   */
  private setForcedDirection(direction: Direction): void {
    this.forcedDirection = direction;
    this.timeSinceLastMove = 0;
  }

  /**
   * Clear forced movement direction (when exiting arrow tile)
   */
  clearForcedDirection(): void {
    this.forcedDirection = undefined;
    this.timeSinceLastMove = 0;
  }

  /**
   * Water blocks move when forced by arrows or when pushed
   */
  move(context: MoveContext): MoveResult {
    let direction: Direction | undefined = undefined;
    let oldTime: number | undefined = undefined;
    if (this.pendingMove) {
      direction = this.pendingMove;
      this.clearPendingMove();
    } else {

      // Check if we're on an arrow tile but not yet in forced mode
      const currentTile = context.getAt(this.coordinates()).tile;
      if (currentTile && isArrowTile(currentTile)) {
        const newDirection = getArrowDirection(currentTile);
        if (this.forcedDirection?.value !== newDirection.value)
          this.setForcedDirection(newDirection);
        //this.timeSinceLastMove = 0;
      }

      // FORCED ARROW MOVEMENT
      if (this.forcedDirection) {
        console.log(`waterblock has forced direction ${this.forcedDirection.value}, timeSinceLastMove=${this.timeSinceLastMove}`, context.deltaMs);
        this.timeSinceLastMove += context.deltaMs;

        if (this.timeSinceLastMove < GAME_CONFIG.arrowTileSpeed) {
          return { type: "wait" };
        }
        oldTime = this.timeSinceLastMove;
        this.timeSinceLastMove = 0;
        direction = this.forcedDirection;
      }
    }

    if (!direction) {
      // No movement this tick
      return { type: "wait" };
    }

    // Check target position
    const target = context.getAtDirection(this.coordinates(), direction);
    console.log(`WaterBlockEntity at (${this.col}, ${this.row}) forced move ${direction.value} to (${target.tile})`);

    if (target.entity === TILE.WATER_BLOCK || target.entity === TILE.BALL) {
      return { type: "push", direction, withMomentum: true };
    } else if (target.tile === TILE.BUTTON) {
      // Moving into water - fill and disappear
      return { type: "pressButton", direction };
    }

    // Check if can move to target
    if (this.canBePushed(target.tile, target.entity)) {
      // Check if we're landing on another arrow
      if (target.tile && isArrowTile(target.tile)) {
        const newDirection = getArrowDirection(target.tile);
        this.setForcedDirection(newDirection);
        //direction = newDirection;
      } else {
        // Landing on non-arrow, clear forced movement
        this.clearForcedDirection();
      }
      return { type: "move", direction };
    } else {
      if (oldTime !== undefined)
        this.timeSinceLastMove = oldTime; // Restore time for next attempt
      return { type: "wall" };
    }
  }

  /**
   * Check if water block can be pushed to a target position
   * This is called externally when something tries to push it
   */
  canBePushed(
    targetTile: TileIndex | null,
    targetEntity: TileIndex | null
  ): boolean {

    console.log(`WaterBlockEntity.canBePushed to tile ${targetTile}, entity ${targetEntity}`);

    if (targetTile === null) {
      return false;
    }

    const actAsWalls: TileIndex[] = [
      TILE.WALL,
      TILE.DOOR_GREEN,
      TILE.DOOR_RED,
      TILE.DOOR_ORANGE,
      TILE.DOOR_BLUE,
      TILE.BUTTON_DOOR,
      TILE.EXIT,
      TILE.BOMB,
      TILE.WATER_BLOCK,
      TILE.SAND
    ]

    if (actAsWalls.includes(targetTile)) {
      return false;
    }


    // Check entity layer - any entity blocks
    if (!isEmpty(targetEntity)) {
      return false;
    }

    // Otherwise can be pushed
    return true;
  }

  clone(): WaterBlockEntity {
    const copy = new WaterBlockEntity({
      col: this.col,
      row: this.row,
      id: this.id,
    });
    copy.forcedDirection = this.forcedDirection;
    copy.timeSinceLastMove = this.timeSinceLastMove;
    copy.sprite = this.sprite; // Share sprite reference
    return copy;
  }

  toJSON(): Record<string, any> {
    return {
      type: "waterblock",
      id: this.id,
      col: this.col,
      row: this.row,
      forcedDirection: this.forcedDirection,
      timeSinceLastMove: this.timeSinceLastMove,
    };
  }
}
